import React, { useState } from 'react';
import type { PendingPayment, OnlineRegistration } from '../../types/db';
import {
  CreditCard,
  CheckCircle,
  UserPlus,
  ImageIcon,
  X,
  Loader2,
  FileText,
  ExternalLink,
  User,
  Phone,
  MapPin,
  Mail,
  Key,
  ShieldAlert,
  AlertCircle,
  ArrowRight,
  Trash2,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import {
  approvePayment,
  rejectPayment,
  approveRegistration,
  rejectRegistration,
} from '../../services/financeService';

export function PaymentVerification({
  payments,
  registrations,
  onRefresh,
}: {
  payments: PendingPayment[];
  registrations: OnlineRegistration[];
  onRefresh: () => void;
}) {
  const [viewingProof, setViewingProof] = useState<string | null>(null);
  const [reviewingReg, setReviewingReg] = useState<OnlineRegistration | null>(null);
  const [loading, setLoading] = useState<string | null>(null);

  // Form State untuk aktivasi pendaftar
  const [activationForm, setActivationForm] = useState({
    email: '',
    password: '',
  });

  const handleOpenProof = async (path?: string) => {
    if (!path) return window.alert('File tidak ditemukan.');
    try {
      setLoading(path);
      const { data, error } = await supabase.storage
        .from('learning-files')
        .createSignedUrl(path, 60);
      if (error) throw error;
      setViewingProof(data.signedUrl);
    } catch (e: any) {
      window.alert('Gagal membuka file: ' + e.message);
    } finally {
      setLoading(null);
    }
  };

  const handleReviewReg = async (reg: OnlineRegistration) => {
    // Auto-generate email based on phone if empty
    const defaultEmail = `${reg.phone_number.replace(/\D/g, '')}@delta.com`;
    setActivationForm({
      email: defaultEmail,
      password: 'bimbeldelta2024', // Default password
    });
    setReviewingReg(reg);
  };

  const handleApprovePaymentProcess = async (id: string) => {
    if (!window.confirm('Verifikasi pembayaran ini? Saldo siswa akan otomatis diperbarui.')) return;
    setLoading(id);
    try {
      await approvePayment(id);
      window.alert('Verifikasi Pembayaran Berhasil!');
      onRefresh();
    } catch (e: any) {
      window.alert('Gagal: ' + e.message);
    } finally {
      setLoading(null);
    }
  };

  const handleRejectPaymentProcess = async (id: string) => {
    if (!window.confirm('Tolak pembayaran ini? Status akan ditandai REJECTED.')) return;
    setLoading(id);
    try {
      await rejectPayment(id);
      onRefresh();
    } catch (e: any) {
      window.alert('Gagal: ' + e.message);
    } finally {
      setLoading(null);
    }
  };

  const handleConfirmActivation = async () => {
    if (!reviewingReg) return;
    if (!activationForm.email || !activationForm.password) {
      return window.alert('Email dan Password wajib diisi untuk pembuatan akun.');
    }

    setLoading(reviewingReg.id);
    try {
      await approveRegistration({
        registration_id: reviewingReg.id,
        email: activationForm.email,
        password: activationForm.password,
      });
      window.alert(
        `Siswa ${reviewingReg.name} berhasil diaktifkan!\nLogin: ${activationForm.email}`
      );
      setReviewingReg(null);
      onRefresh();
    } catch (e: any) {
      window.alert('Gagal Aktivasi: ' + e.message);
    } finally {
      setLoading(null);
    }
  };

  const handleRejectRegistrationProcess = async (id: string) => {
    if (!window.confirm('Tolak pendaftaran ini? Data akan ditandai REJECTED.')) return;
    setLoading(id);
    try {
      await rejectRegistration(id);
      setReviewingReg(null);
      onRefresh();
    } catch (e: any) {
      window.alert('Gagal: ' + e.message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="space-y-12 animate-fade-in font-sans">
      {/* 1. Pembayaran Siswa Aktif */}
      <section className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-50 text-orange-500 rounded-xl flex items-center justify-center shadow-sm">
              <CreditCard className="w-5 h-5" />
            </div>
            <h3 className="font-black text-gray-900 text-xl uppercase tracking-tight">
              Verifikasi Pembayaran Siswa
            </h3>
          </div>
          <span className="text-[9px] font-black bg-orange-100 text-orange-600 px-3 py-1 rounded-full uppercase tracking-widest">
            {payments.length} Antrian
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {payments.map((p) => (
            <div
              key={p.id}
              className="p-8 bg-white border border-gray-100 rounded-[2.5rem] flex flex-col md:flex-row justify-between items-center shadow-sm hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-6 w-full md:w-auto">
                <button
                  onClick={() => handleOpenProof(p.transfer_proof_path)}
                  disabled={loading === p.transfer_proof_path}
                  className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-500 border border-orange-100 group-hover:scale-105 transition-transform shrink-0 relative shadow-inner"
                >
                  {loading === p.transfer_proof_path ? (
                    <Loader2 className="animate-spin w-5 h-5" />
                  ) : (
                    <ImageIcon className="w-7 h-7" />
                  )}
                </button>
                <div>
                  <p className="font-black text-lg text-gray-900 uppercase tracking-tight">
                    {p.student_name}
                  </p>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1 flex items-center gap-2">
                    <FileText className="w-3 h-3 text-indigo-400" /> {p.notes || 'Pembayaran SPP'} •{' '}
                    {p.date}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-6 md:mt-0 w-full md:w-auto justify-between border-t md:border-t-0 pt-4 md:pt-0">
                <p className="font-black text-2xl text-indigo-600 mr-4">
                  Rp {p.amount.toLocaleString()}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleRejectPaymentProcess(p.id)}
                    className="p-3 bg-rose-50 text-rose-400 hover:text-rose-600 rounded-xl transition-all"
                    title="Tolak Pembayaran"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleApprovePaymentProcess(p.id)}
                    disabled={!!loading}
                    className="bg-emerald-600 text-white px-8 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-emerald-100 hover:bg-emerald-700 transition-all flex items-center gap-2 disabled:opacity-50"
                  >
                    {loading === p.id ? (
                      <Loader2 className="animate-spin w-4 h-4" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}{' '}
                    VERIFIKASI
                  </button>
                </div>
              </div>
            </div>
          ))}
          {payments.length === 0 && (
            <div className="py-24 text-center text-gray-300 font-black uppercase tracking-[0.3em] text-[10px] border-4 border-dashed rounded-[3rem] border-gray-50 bg-slate-50/20">
              Antrian Pembayaran Kosong
            </div>
          )}
        </div>
      </section>

      {/* 2. Registrasi Online Baru */}
      <section className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 text-blue-500 rounded-xl flex items-center justify-center shadow-sm">
              <UserPlus className="w-5 h-5" />
            </div>
            <h3 className="font-black text-gray-900 text-xl uppercase tracking-tight">
              Pendaftaran Online Baru
            </h3>
          </div>
          <span className="text-[9px] font-black bg-blue-100 text-blue-600 px-3 py-1 rounded-full uppercase tracking-widest">
            {registrations.length} Pendaftar
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {registrations.map((r) => (
            <div
              key={r.id}
              className="p-8 bg-white border border-gray-100 rounded-[2.5rem] flex flex-col md:flex-row justify-between items-center shadow-sm hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-6 w-full md:w-auto">
                <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-500 border border-blue-100 shadow-inner">
                  <User className="w-7 h-7" />
                </div>
                <div>
                  <p className="font-black text-lg text-gray-900 uppercase tracking-tight">
                    {r.name}
                  </p>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1 flex items-center gap-2 flex-wrap">
                    <MapPin className="w-3 h-3 text-indigo-300" /> {r.branch_id} •{' '}
                    {r.program_choice} • {r.grade}
                    <span
                      className={`ml-2 px-2 py-0.5 rounded-md font-black text-[8px] ${r.payment_method === 'ANGSUR' ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'}`}
                    >
                      {r.payment_method === 'ANGSUR'
                        ? `ANGSURAN (${r.installment_count}X)`
                        : 'CASH'}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-6 md:mt-0 w-full md:w-auto justify-between border-t md:border-t-0 pt-4 md:pt-0">
                <p className="font-black text-2xl text-indigo-600 mr-4">
                  Rp {r.total_cost.toLocaleString()}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleRejectRegistrationProcess(r.id)}
                    className="p-3 bg-rose-50 text-rose-400 hover:text-rose-600 rounded-xl transition-all"
                    title="Hapus / Tolak"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleReviewReg(r)}
                    disabled={!!loading}
                    className="bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center gap-2"
                  >
                    DETAIL & AKTIFKAN <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {registrations.length === 0 && (
            <div className="py-24 text-center text-gray-300 font-black uppercase tracking-[0.3em] text-[10px] border-4 border-dashed rounded-[3rem] border-gray-50 bg-slate-50/20">
              Tidak ada pendaftar baru
            </div>
          )}
        </div>
      </section>

      {/* 🚀 MODAL REVIEW REGISTRASI (DETAIL + KREDENSIAL) */}
      {reviewingReg && (
        <div className="fixed inset-0 bg-slate-900/70 z-[300] flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-[3.5rem] w-full max-w-5xl shadow-2xl animate-fade-in-up border border-white overflow-hidden flex flex-col md:flex-row max-h-[95vh]">
            {/* Sisi Kiri: Bukti Transfer & Info Biaya */}
            <div className="md:w-2/5 bg-slate-50 p-10 border-r border-slate-100 flex flex-col justify-between overflow-y-auto no-scrollbar">
              <div className="space-y-8">
                <div>
                  <h4 className="font-black text-xs text-indigo-600 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" /> Bukti Pembayaran
                  </h4>
                  <div className="aspect-[4/5] bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-inner group relative flex items-center justify-center">
                    {loading === 'PREVIEW' ? (
                      <Loader2 className="animate-spin text-slate-300 w-10 h-10" />
                    ) : viewingProof ? (
                      <>
                        <img
                          src={viewingProof}
                          className="w-full h-full object-cover"
                          alt="Bukti Transfer"
                        />
                        <a
                          href={viewingProof}
                          target="_blank"
                          rel="noreferrer"
                          className="absolute bottom-4 right-4 bg-white/90 p-2 rounded-xl shadow-lg text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all"
                        >
                          <ExternalLink className="w-5 h-5" />
                        </a>
                      </>
                    ) : (
                      <div className="text-center p-6">
                        <button
                          onClick={() => handleOpenProof(reviewingReg.transfer_proof_path)}
                          className="bg-indigo-600 text-white px-6 py-3 rounded-2xl font-black uppercase text-[9px] tracking-widest shadow-lg"
                        >
                          Lihat Foto Bukti
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
                  <div className="flex justify-between items-end">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                      Total Tagihan
                    </p>
                    <p className="text-2xl font-black text-indigo-600 tracking-tighter">
                      Rp {reviewingReg.total_cost.toLocaleString()}
                    </p>
                  </div>
                  <div className="pt-3 border-t border-slate-50 text-[10px] font-bold text-slate-500 italic">
                    Pembayaran registrasi dan angsuran pertama (DP) yang telah dikirim via sistem
                    Delta Online.
                  </div>
                </div>
              </div>
            </div>

            {/* Sisi Kanan: Data Lengkap & Form Aktivasi */}
            <div className="md:w-3/5 p-10 md:p-14 bg-white overflow-y-auto no-scrollbar">
              <div className="flex justify-between items-start mb-10">
                <div>
                  <h3 className="text-3xl font-black text-slate-900 uppercase tracking-tighter leading-none">
                    Review Pendaftar
                  </h3>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-3">
                    ID: {reviewingReg.id}
                  </p>
                </div>
                <button
                  onClick={() => setReviewingReg(null)}
                  className="p-3 bg-slate-50 text-slate-300 hover:text-slate-900 rounded-full transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-8 mb-12">
                <div className="space-y-6">
                  <h5 className="font-black text-[10px] text-indigo-600 uppercase tracking-widest border-b pb-2 flex items-center gap-2">
                    <User className="w-3 h-3" /> Data Siswa
                  </h5>
                  <DetailRow label="Nama Lengkap" value={reviewingReg.name} />
                  <DetailRow label="Unit Cabang" value={reviewingReg.branch_id} />
                  <DetailRow label="Tingkat Kelas" value={reviewingReg.grade} />
                  <DetailRow label="Program" value={reviewingReg.program_choice} />
                  <DetailRow
                    label="Metode Bayar"
                    value={
                      reviewingReg.payment_method === 'ANGSUR'
                        ? `ANGSURAN (${reviewingReg.installment_count}X)`
                        : 'CASH'
                    }
                  />
                  <DetailRow label="WA Siswa" value={reviewingReg.phone_number} />
                  <DetailRow label="Asal Sekolah" value={reviewingReg.school_origin || '-'} />
                </div>
                <div className="space-y-6">
                  <h5 className="font-black text-[10px] text-emerald-600 uppercase tracking-widest border-b pb-2 flex items-center gap-2">
                    <Phone className="w-3 h-3" /> Kontak Ortu
                  </h5>
                  <DetailRow label="Nama Orang Tua" value={reviewingReg.parent_name || '-'} />
                  <DetailRow label="WA Orang Tua" value={reviewingReg.parent_phone || '-'} />
                  <DetailRow label="Alamat" value={reviewingReg.address || '-'} />
                </div>
              </div>

              <div className="bg-indigo-50 p-8 rounded-[3rem] border border-indigo-100 shadow-inner relative overflow-hidden space-y-6">
                <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-600/5 rounded-bl-full"></div>
                <h5 className="font-black text-xs text-indigo-900 uppercase tracking-widest flex items-center gap-3">
                  <Key className="w-5 h-5 text-indigo-500" /> Setup Akses Login Siswa
                </h5>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[9px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                      Email Login (Manual)
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                      <input
                        className="w-full border-2 border-white pl-12 pr-4 py-4 rounded-2xl font-bold bg-white text-xs outline-none focus:ring-4 focus:ring-indigo-500/10"
                        value={activationForm.email}
                        onChange={(e) =>
                          setActivationForm({ ...activationForm, email: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[9px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                      Password Default
                    </label>
                    <div className="relative">
                      <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                      <input
                        className="w-full border-2 border-white pl-12 pr-4 py-4 rounded-2xl font-bold bg-white text-xs outline-none focus:ring-4 focus:ring-indigo-500/10"
                        value={activationForm.password}
                        onChange={(e) =>
                          setActivationForm({ ...activationForm, password: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => handleRejectRegistrationProcess(reviewingReg.id)}
                    disabled={!!loading}
                    className="flex-1 bg-rose-50 text-rose-500 py-5 rounded-3xl font-black uppercase text-[10px] tracking-widest hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center gap-3"
                  >
                    {loading === reviewingReg.id ? (
                      <Loader2 className="animate-spin w-4 h-4" />
                    ) : (
                      <ShieldAlert className="w-4 h-4" />
                    )}
                    TOLAK REGISTRASI
                  </button>
                  <button
                    onClick={handleConfirmActivation}
                    disabled={!!loading}
                    className="flex-[2] bg-indigo-600 text-white py-5 rounded-3xl font-black uppercase text-[10px] tracking-[0.2em] shadow-xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-3 active:scale-95"
                  >
                    {loading === reviewingReg.id ? (
                      <Loader2 className="animate-spin w-4 h-4" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    AKTIFKAN SEKARANG
                  </button>
                </div>
              </div>

              <div className="mt-8 flex items-center gap-4 text-slate-400 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                <AlertCircle className="w-5 h-5 shrink-0" />
                <p className="text-[10px] font-bold leading-relaxed uppercase tracking-tight">
                  Aktivasi akan membuat profil login SISWA secara permanen dan memindahkan data
                  pendaftaran ke database aktif Bimbel Delta.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal Standar (Untuk Bukti Transfer Biasa) */}
      {viewingProof && !reviewingReg && (
        <div
          className="fixed inset-0 bg-slate-900/90 z-[1000] flex flex-col items-center justify-center p-4 backdrop-blur-md"
          onClick={() => setViewingProof(null)}
        >
          <div
            className="relative max-w-2xl w-full bg-white rounded-[3rem] p-4 shadow-2xl animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setViewingProof(null)}
              className="absolute -top-4 -right-4 bg-white text-slate-900 p-2 rounded-full shadow-lg hover:bg-indigo-600 hover:text-white transition-all"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={viewingProof}
              className="w-full h-auto max-h-[75vh] object-contain rounded-2xl"
              alt="Bukti Transfer"
            />
            <div className="p-6 text-center">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-4">
                Verifikasi Manual Bukti Transfer
              </p>
              <a
                href={viewingProof}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-indigo-600 font-black uppercase text-[10px] tracking-widest hover:text-indigo-700"
              >
                Buka di Tab Baru <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</p>
      <p className="font-black text-slate-800 text-xs uppercase leading-tight">{value}</p>
    </div>
  );
}
